/**
 * Panel geometry for direct manipulation: knife cuts, gutters, and merges.
 *
 * Panels are rects ({x, y, width, height} in page px). A panel produced by an
 * angled knife cut also carries `polygon`: its outline as [[x, y], …] in
 * panel-local px. The rect is always the polygon's bounding box, so every
 * rect-based system (edge drags, relayout, snapping) keeps working; whenever
 * the rect changes, the polygon is rescaled into it.
 */
import { CELL_MIN_SIZE } from "../constants/theme";

const EPSILON = 0.5;
// Strokes within this many degrees of horizontal/vertical cut straight
export const KNIFE_SNAP_DEGREES = 5;
// Each half of a cut must be at least this big in both directions
export const MIN_CUT_SIZE = 32;

// ─── Polygons ───────────────────────────────────────────────────────────

const rectPoints = ({ x, y, width, height }) => [
  [x, y], [x + width, y], [x + width, y + height], [x, y + height],
];

/** A panel's outline in page coordinates. */
export const cellOutline = (cell) =>
  cell.polygon
    ? cell.polygon.map(([px, py]) => [cell.x + px, cell.y + py])
    : rectPoints(cell);

export const polygonArea = (points) => {
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    sum += x1 * y2 - x2 * y1;
  }
  return Math.abs(sum) / 2;
};

const boundsOf = (points) => {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
};

/** True when the outline is just its own bounding box. */
const isAxisAlignedRect = (points, bounds) =>
  points.length === 4 && points.every(([px, py]) =>
    (Math.abs(px - bounds.x) < EPSILON || Math.abs(px - bounds.x - bounds.width) < EPSILON)
    && (Math.abs(py - bounds.y) < EPSILON || Math.abs(py - bounds.y - bounds.height) < EPSILON));

/** Drop consecutive duplicate points left behind by clipping. */
const dedupe = (points) => points.filter((p, i) => {
  const q = points[(i + 1) % points.length];
  return Math.abs(p[0] - q[0]) > 0.01 || Math.abs(p[1] - q[1]) > 0.01;
});

/**
 * Sutherland–Hodgman against one half-plane: keeps the part of a convex
 * polygon where nx·x + ny·y >= c.
 */
export const clipToHalfPlane = (points, nx, ny, c) => {
  const out = [];
  for (let i = 0; i < points.length; i++) {
    const cur = points[i];
    const next = points[(i + 1) % points.length];
    const dCur = nx * cur[0] + ny * cur[1] - c;
    const dNext = nx * next[0] + ny * next[1] - c;
    if (dCur >= 0) out.push(cur);
    if ((dCur >= 0) !== (dNext >= 0)) {
      const t = dCur / (dCur - dNext);
      out.push([cur[0] + t * (next[0] - cur[0]), cur[1] + t * (next[1] - cur[1])]);
    }
  }
  return dedupe(out);
};

/** Geometry fields for a panel whose outline is `points` (page coords). */
const geometryFromOutline = (points) => {
  const bounds = boundsOf(points);
  if (isAxisAlignedRect(points, bounds)) return { ...bounds, polygon: undefined };
  return {
    ...bounds,
    polygon: points.map(([px, py]) => [px - bounds.x, py - bounds.y]),
  };
};

/** Move/resize a panel's rect, rescaling its polygon (if any) to match. */
export const withRect = (cell, rect) => {
  const next = { ...cell, ...rect };
  if (cell.polygon && (rect.width !== cell.width || rect.height !== cell.height)) {
    const sx = cell.width > 0 ? rect.width / cell.width : 1;
    const sy = cell.height > 0 ? rect.height / cell.height : 1;
    next.polygon = cell.polygon.map(([px, py]) => [px * sx, py * sy]);
  }
  return next;
};

/** CSS clip-path for a polygon panel. */
export const polygonClipPath = (polygon) =>
  `polygon(${polygon.map(([px, py]) => `${px}px ${py}px`).join(", ")})`;

// ─── Knife ──────────────────────────────────────────────────────────────

/**
 * Turn a knife stroke into the line that will be cut. Nearly-straight
 * strokes (or any stroke with `straight`) snap to horizontal/vertical.
 * Returns { p0, p1, orientation } or null for a stroke too short to read.
 */
export const resolveKnifeLine = (start, end, { straight = false } = {}) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (Math.hypot(dx, dy) < 6) return null;
  const angle = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI) % 180;
  const fromHorizontal = Math.min(angle, 180 - angle);
  const fromVertical = Math.abs(90 - angle);
  if (straight || fromHorizontal <= KNIFE_SNAP_DEGREES || fromVertical <= KNIFE_SNAP_DEGREES) {
    if (fromHorizontal <= fromVertical) {
      const y = (start.y + end.y) / 2;
      return { p0: { x: start.x, y }, p1: { x: end.x, y }, orientation: "horizontal" };
    }
    const x = (start.x + end.x) / 2;
    return { p0: { x, y: start.y }, p1: { x, y: end.y }, orientation: "vertical" };
  }
  return { p0: start, p1: end, orientation: "angled" };
};

/** Where the (infinite) knife line crosses a panel, for previewing a cut. */
export const knifeSegmentInCell = (cell, p0, p1) => {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const nx = -dy / len;
  const ny = dx / len;
  const c = nx * p0.x + ny * p0.y;
  const outline = cellOutline(cell);
  const hits = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    const da = nx * a[0] + ny * a[1] - c;
    const db = nx * b[0] + ny * b[1] - c;
    if ((da >= 0) !== (db >= 0)) {
      const t = da / (da - db);
      hits.push({ x: a[0] + t * (b[0] - a[0]), y: a[1] + t * (b[1] - a[1]) });
    }
  }
  return hits.length >= 2 ? [hits[0], hits[1]] : null;
};

// Approximate footprint of an item, for deciding which side of a cut it's on
const itemCentre = (item) => {
  if (item.type === "sfx") {
    const fontSize = item.fontSize || 64;
    const w = (item.text || "BOOM").length * fontSize * 0.6 * (item.scaleX ?? 1);
    const h = fontSize * 1.2 * (item.scaleY ?? 1);
    return [(item.x ?? 20) + w / 2, (item.y ?? 20) + h / 2];
  }
  return [(item.x ?? 16) + (item.width ?? 160) / 2, (item.y ?? 16) + (item.height ?? 96) / 2];
};

let cutCounter = 0;
const newCellId = () => `cell-cut-${Date.now().toString(36)}-${(cutCounter++).toString(36)}`;

/**
 * Split a panel along the knife line through p0→p1, leaving a gutter of
 * `gutter` px centred on the line. Bubbles and SFX stay where they are on
 * the page and go to whichever side their centre is on; the background art
 * stays with the larger half. Returns [keptCell, newCell] or null when the
 * line misses the panel or either half would be too small.
 */
export const splitCell = (cell, p0, p1, gutter) => {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  const nx = -dy / len;
  const ny = dx / len;
  const c = nx * p0.x + ny * p0.y;
  const outline = cellOutline(cell);

  const sideA = clipToHalfPlane(outline, nx, ny, c + gutter / 2);
  const sideB = clipToHalfPlane(outline, -nx, -ny, -(c - gutter / 2));
  if (sideA.length < 3 || sideB.length < 3) return null;

  const geomA = geometryFromOutline(sideA);
  const geomB = geometryFromOutline(sideB);
  const bigEnough = (g) => g.width >= MIN_CUT_SIZE && g.height >= MIN_CUT_SIZE;
  if (!bigEnough(geomA) || !bigEnough(geomB)) return null;

  // Panel-level styling carries to both halves; a preset clip shape doesn't
  // survive being cut, and merge history no longer applies.
  // eslint-disable-next-line no-unused-vars
  const { id, key, x, y, width, height, polygon, items = [], panelShape, mergedFrom, ...styling } = cell;
  const aIsLarger = polygonArea(sideA) >= polygonArea(sideB);

  const itemsFor = (geom, isA) => items
    .filter((item) => {
      if (item.type === "background") return isA === aIsLarger;
      const [cx, cy] = itemCentre(item);
      const onA = nx * (cell.x + cx) + ny * (cell.y + cy) - c >= 0;
      return onA === isA;
    })
    .map((item) => (item.type === "background" || item.x == null ? item : {
      ...item,
      x: item.x + cell.x - geom.x,
      y: item.y + cell.y - geom.y,
    }));

  const keyBase = key || id;
  const halfA = { ...styling, ...geomA, items: itemsFor(geomA, true) };
  const halfB = { ...styling, ...geomB, items: itemsFor(geomB, false) };
  if (!halfA.polygon) delete halfA.polygon;
  if (!halfB.polygon) delete halfB.polygon;

  const kept = aIsLarger ? halfA : halfB;
  const added = aIsLarger ? halfB : halfA;
  return [
    { ...kept, id, key: `${keyBase}a` },
    { ...added, id: newCellId(), key: `${keyBase}b` },
  ];
};

// ─── Gutters ────────────────────────────────────────────────────────────

/**
 * Find the gutters you can grab: runs of panels facing each other across
 * exactly one gutter width. A run continues across crossing gutters, so in
 * a grid each column line and each row line is one gutter.
 *
 * Returns [{ id, axis, at, start, end, before, after }]: `axis` "x" is a
 * vertical gutter moved left/right; `at` is the gutter's leading edge;
 * start/end span it along the other axis; before/after are panel ids.
 */
export const findGutters = (cells, gutter, tolerance = 1.5) => {
  const runs = [];
  ["x", "y"].forEach((axis) => {
    const pos = axis === "x" ? "x" : "y";
    const size = axis === "x" ? "width" : "height";
    const crossPos = axis === "x" ? "y" : "x";
    const crossSize = axis === "x" ? "height" : "width";

    const pairs = [];
    cells.forEach((a) => {
      cells.forEach((b) => {
        if (a === b) return;
        const gap = b[pos] - (a[pos] + a[size]);
        if (Math.abs(gap - gutter) > tolerance) return;
        const start = Math.max(a[crossPos], b[crossPos]);
        const end = Math.min(a[crossPos] + a[crossSize], b[crossPos] + b[crossSize]);
        if (end - start <= EPSILON) return;
        pairs.push({ at: a[pos] + a[size], start, end, a: a.id, b: b.id });
      });
    });

    // Group pairs on the same line, then join neighbouring stretches
    pairs.sort((p, q) => p.at - q.at || p.start - q.start);
    pairs.forEach((pair) => {
      const run = runs.find((r) => r.axis === axis
        && Math.abs(r.at - pair.at) <= tolerance
        && pair.start <= r.end + gutter + tolerance
        && pair.end >= r.start - gutter - tolerance);
      if (run) {
        run.start = Math.min(run.start, pair.start);
        run.end = Math.max(run.end, pair.end);
        if (!run.before.includes(pair.a)) run.before.push(pair.a);
        if (!run.after.includes(pair.b)) run.after.push(pair.b);
        run.pairs.push(pair);
      } else {
        runs.push({ axis, at: pair.at, start: pair.start, end: pair.end, before: [pair.a], after: [pair.b], pairs: [pair] });
      }
    });
  });
  return runs.map((run, i) => ({ ...run, id: `${run.axis}-${Math.round(run.at)}-${i}` }));
};

/** How far a gutter can travel before a panel gets too small: [min, max]. */
export const gutterTravel = (cells, run) => {
  const size = run.axis === "x" ? "width" : "height";
  const byId = new Map(cells.map((c) => [c.id, c]));
  const shrinkBefore = Math.min(...run.before.map((id) => byId.get(id)[size] - CELL_MIN_SIZE));
  const shrinkAfter = Math.min(...run.after.map((id) => byId.get(id)[size] - CELL_MIN_SIZE));
  return [-Math.max(0, shrinkBefore), Math.max(0, shrinkAfter)];
};

/** Slide a gutter by `delta` px (clamped), resizing the panels on both sides. */
export const moveGutter = (cells, run, delta) => {
  const [min, max] = gutterTravel(cells, run);
  const d = Math.min(max, Math.max(min, delta));
  if (d === 0) return cells;
  const pos = run.axis === "x" ? "x" : "y";
  const size = run.axis === "x" ? "width" : "height";
  return cells.map((cell) => {
    if (run.before.includes(cell.id)) {
      return withRect(cell, { ...rectOf(cell), [size]: cell[size] + d });
    }
    if (run.after.includes(cell.id)) {
      return withRect(cell, { ...rectOf(cell), [pos]: cell[pos] + d, [size]: cell[size] - d });
    }
    return cell;
  });
};

const rectOf = (c) => ({ x: c.x, y: c.y, width: c.width, height: c.height });

// ─── Merge ──────────────────────────────────────────────────────────────

const relocateItems = (items, fromCell, to) => (items || []).map((item) =>
  (item.type === "background" || item.x == null ? item : {
    ...item, x: item.x + fromCell.x - to.x, y: item.y + fromCell.y - to.y,
  }));

const snapshotOf = (c) => ({
  id: c.id, key: c.key, x: c.x, y: c.y, width: c.width, height: c.height,
  ...(c.polygon ? { polygon: c.polygon } : {}),
});

/**
 * Merge panels into one rect covering them all. Every bubble and SFX keeps
 * its place on the page; the first panel's background (if any) survives.
 * Returns null if the combined rect would run into another panel.
 */
export const mergeCellGroup = (cells, ids, gutter) => {
  const group = ids.map((id) => cells.find((c) => c.id === id)).filter(Boolean);
  if (group.length < 2) return null;
  const x = Math.min(...group.map((c) => c.x));
  const y = Math.min(...group.map((c) => c.y));
  const right = Math.max(...group.map((c) => c.x + c.width));
  const bottom = Math.max(...group.map((c) => c.y + c.height));
  const merged = { x, y, width: right - x, height: bottom - y };

  const blocked = cells.some((c) => !ids.includes(c.id)
    && c.x < merged.x + merged.width + gutter - EPSILON
    && c.x + c.width + gutter - EPSILON > merged.x
    && c.y < merged.y + merged.height + gutter - EPSILON
    && c.y + c.height + gutter - EPSILON > merged.y);
  if (blocked) return null;

  const allItems = group.flatMap((c) => relocateItems(c.items, c, merged));
  const background = allItems.find((i) => i.type === "background");
  const content = allItems.filter((i) => i.type !== "background");

  const [primary] = group;
  // eslint-disable-next-line no-unused-vars
  const { polygon, mergedFrom, ...styling } = primary;
  const result = {
    ...styling,
    ...merged,
    items: background ? [background, ...content] : content,
    mergedFrom: group.map(snapshotOf),
  };
  return cells
    .filter((c) => c.id === primary.id || !ids.includes(c.id))
    .map((c) => (c.id === primary.id ? result : c));
};

export const mergeCells = (cells, idA, idB, gutter) => mergeCellGroup(cells, [idA, idB], gutter);

/**
 * Undo a merge: restore the original panels (angled outlines included) and
 * send each bubble/SFX back to whichever panel its centre is in.
 */
export const unmergeCell = (cells, id) => {
  const cell = cells.find((c) => c.id === id);
  if (!cell?.mergedFrom?.length) return null;
  // eslint-disable-next-line no-unused-vars
  const { mergedFrom, polygon, items = [], ...styling } = cell;
  const restored = mergedFrom.map((orig) => ({
    ...styling, ...orig, items: [],
  }));
  const contains = (c, px, py) => px >= c.x && px <= c.x + c.width && py >= c.y && py <= c.y + c.height;
  items.forEach((item) => {
    if (item.type === "background" || item.x == null) {
      const home = restored.find((r) => r.id === cell.id) || restored[0];
      home.items.push(item);
      return;
    }
    const [cx, cy] = itemCentre(item);
    const px = cell.x + cx;
    const py = cell.y + cy;
    const home = restored.find((r) => contains(r, px, py)) || restored.find((r) => r.id === cell.id) || restored[0];
    home.items.push(...relocateItems([item], cell, home));
  });
  return [...cells.filter((c) => c.id !== id), ...restored];
};
